import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/#services", label: "Services" },
  { to: "/#about", label: "About" },
  { to: "/#industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo-link"
          aria-label="AdVeda Advertising — Home"
          className="flex items-center group"
        >
          <span className="inline-flex items-center bg-white px-3 py-1.5 md:px-4 md:py-2 transition-transform group-hover:scale-[1.02]">
            <img
              src="/brand/adveda-logo-light.png"
              alt="AdVeda Advertising"
              className="h-6 md:h-7 w-auto select-none"
              draggable="false"
            />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          {links.map((l) =>
            l.to.startsWith("/#") ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="hover-line text-sm uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `hover-line text-sm uppercase tracking-[0.18em] transition-colors ${
                    isActive ? "text-[#d40202]" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <Link
          to="/contact#inquiry-form"
          data-testid="navbar-cta-contact"
          className="hidden lg:inline-flex items-center gap-2 bg-[#d40202] hover:bg-white hover:text-black text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold whitespace-nowrap transition-colors"
        >
          Start a Project
          <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/20 text-white hover:bg-[#d40202] hover:border-[#d40202] transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0a0a0a] border-t border-white/10"
        >
          <nav className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-lg uppercase tracking-widest font-display font-bold text-white hover:text-[#d40202] transition-colors"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact#inquiry-form"
              data-testid="mobile-cta-contact"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#d40202] text-white px-5 py-4 text-xs uppercase tracking-[0.18em] font-bold"
            >
              Start a Project <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
